import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import EventChangeRequestModal from '../EventChangeRequestModal/EventChangeRequestModal'
import { EventPost } from '../events.helper'
import { EventPostModalUiAction, EventPostModalUiState, EventPostModalWrite } from './eventPostModalUi'

type EventPostModalDialogsProps = {
  uiState: EventPostModalUiState
  unsavedEvent: boolean
  eventPost: EventPost
  statusDraft: boolean
  disabledSaveDraft: boolean
  closeModal: () => void
  deleteEvent: (id: number) => Promise<void>
  resetEventPost: () => void
  draftEvent: (data: EventPost) => Promise<void>
  removeEventPropertyField: (index: number) => void
  requestWrite: (write: EventPostModalWrite) => void
  runWrite: (write: EventPostModalWrite) => void
  hasWithheldServerUpdate: boolean
  dispatchUi: React.Dispatch<EventPostModalUiAction>
  CHANGE_REQUEST_PERMISSION: boolean
  hasChangeRequest: boolean
  setEventPost: React.Dispatch<React.SetStateAction<EventPost>>
}

export function EventPostModalDialogs({
  uiState,
  unsavedEvent,
  eventPost,
  statusDraft,
  disabledSaveDraft,
  closeModal,
  deleteEvent,
  resetEventPost,
  draftEvent,
  removeEventPropertyField,
  requestWrite,
  runWrite,
  hasWithheldServerUpdate,
  dispatchUi,
  CHANGE_REQUEST_PERMISSION,
  hasChangeRequest,
  setEventPost,
}: EventPostModalDialogsProps): ReactNode {
  const { t } = useTranslation()

  // Closing a dirty draft already offers Save draft, so the overwrite decision belongs on that
  // button rather than in a second dialog stacked on this one.
  const saveDraftButtonText = hasWithheldServerUpdate ? 'Save draft, overwriting their version' : 'Save draft'
  const overwriteSubText = hasWithheldServerUpdate
    ? [t('Saving this draft replaces the version someone else saved since you started editing.')]
    : undefined
  const deleteSubText = hasWithheldServerUpdate
    ? [t('Someone else has saved this event since you started editing. Deleting also removes their version.')]
    : undefined

  return (
    <>
      <ConfirmDialog
        open={uiState.confirmDeleteModalOpen}
        onConfirm={() => {
          dispatchUi({ type: 'set', key: 'confirmDeleteModalOpen', value: false })

          if (unsavedEvent) {
            closeModal()

            return
          }

          // `deleteEvent` closes the modal itself, once the DELETE has landed. Closing here as
          // well showed a confirmed delete as done even when its write guard dropped the request.
          deleteEvent(eventPost?.id as number)
        }}
        onClose={() => dispatchUi({ type: 'set', key: 'confirmDeleteModalOpen', value: false })}
        subTextList={deleteSubText}
        textObject={{
          title: t(`Are you sure you want to remove this event?`),
          text: t(`This event and it's settings will be deleted and you will not be able to recover it.`),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmOnCloseModalOpen}
        onConfirm={() => {
          resetEventPost()
          dispatchUi({ type: 'set', key: 'confirmOnCloseModalOpen', value: false })
          closeModal()
        }}
        onClose={() => dispatchUi({ type: 'set', key: 'confirmOnCloseModalOpen', value: false })}
        subTextList={overwriteSubText}
        textObject={{
          title: t(`Are you sure you want to close this window?`),
          text: t(`All your progress will be lost if you continue.`),
          confirm: t('Close without saving'),
        }}
        alternativeButton={
          statusDraft && !disabledSaveDraft
            ? {
                text: saveDraftButtonText,
                onClick: () => {
                  draftEvent(eventPost)
                  dispatchUi({ type: 'set', key: 'confirmOnCloseModalOpen', value: false })
                  closeModal()
                },
              }
            : undefined
        }
      />
      <ConfirmDialog
        open={uiState.confirmOnThrowAwayModalOpen}
        onConfirm={() => {
          dispatchUi({ type: 'set', key: 'confirmOnThrowAwayModalOpen', value: false })

          if (unsavedEvent) {
            resetEventPost()
            closeModal()

            return
          }

          deleteEvent(eventPost.id as number)
        }}
        onClose={() => dispatchUi({ type: 'set', key: 'confirmOnThrowAwayModalOpen', value: false })}
        subTextList={overwriteSubText}
        textObject={{
          title: t(`Are you sure you want to throw away this event?`),
          text: t('All your progress will be lost if you continue.'),
          confirm: t('Yes, throw away'),
        }}
        alternativeButton={
          statusDraft && !disabledSaveDraft
            ? {
                text: saveDraftButtonText,
                onClick: () => {
                  draftEvent(eventPost)
                  dispatchUi({ type: 'set', key: 'confirmOnThrowAwayModalOpen', value: false })
                  closeModal()
                },
              }
            : undefined
        }
      />
      <ConfirmDialog
        open={uiState.confirmOnRemoveAffectedComponentModalOpen}
        onConfirm={() => {
          removeEventPropertyField(uiState.selectedAffectedComponent)
          dispatchUi({ type: 'set', key: 'confirmOnRemoveAffectedComponentModalOpen', value: false })
          dispatchUi({ type: 'setSelectedAffectedComponent', value: -1 })
        }}
        onClose={() => {
          dispatchUi({ type: 'set', key: 'confirmOnRemoveAffectedComponentModalOpen', value: false })
          dispatchUi({ type: 'setSelectedAffectedComponent', value: -1 })
        }}
        textObject={{
          title: t(`Are you sure you want to remove this affected component?`),
          text: t('If you remove this affected component, all settings connected to it will be removed as well.'),
          confirm: t('Yes, remove'),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmSaveEventWithChangeRequest}
        onConfirm={() => {
          requestWrite('save')
          dispatchUi({ type: 'set', key: 'confirmSaveEventWithChangeRequest', value: false })
        }}
        onClose={() => {
          dispatchUi({ type: 'set', key: 'confirmSaveEventWithChangeRequest', value: false })
        }}
        textObject={{
          title: t(`This event has a pending change request`),
          text: t(
            'Note that this event has a pending change request and your changes could be overwritten if the change request is approved. Do you want to continue saving your changes?'
          ),
          confirm: t('Save'),
        }}
      />

      <ConfirmDialog
        open={uiState.pendingWrite !== null}
        initialFocus="deny"
        onConfirm={() => {
          const pendingWrite = uiState.pendingWrite
          dispatchUi({ type: 'setPendingWrite', value: null })
          if (pendingWrite) runWrite(pendingWrite)
        }}
        onClose={() => dispatchUi({ type: 'setPendingWrite', value: null })}
        textObject={{
          title: t('This event has been updated by someone else'),
          text: t(
            'Someone else has saved this event since you started editing. The form still shows your changes, and saving replaces their version with yours. To see their version instead, cancel your edit and reopen the event.'
          ),
          confirm: t('Overwrite their version'),
          deny: t('Cancel'),
        }}
      />

      {uiState.openEventEditRequest && CHANGE_REQUEST_PERMISSION && (
        <EventChangeRequestModal
          eventPost={eventPost}
          closeModal={() => dispatchUi({ type: 'set', key: 'openEventEditRequest', value: false })}
          mode={hasChangeRequest ? 'read-only' : 'new'}
          onEventUpdated={(updatedEventPost) => {
            setEventPost(updatedEventPost)
          }}
        />
      )}
    </>
  )
}
